import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Loader2, Users, Download } from 'lucide-react';
import { saveTeamMember, getTeamMembers, deleteTeamMember } from '../../firebase/collections';
import toast from 'react-hot-toast';

const TeamManagement = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    position: 'Development',
    avatar: '',
    color: 'bg-gradient-to-r from-blue-600 to-purple-600',
    order: 0
  });

  const positions = [
    'Leadership',
    'Development',
    'Design',
    'Content',
    'Marketing',
    'Database',
    'Business',
    'Support'
  ];

  const colorOptions = [
    { label: 'Blue to Purple', value: 'bg-gradient-to-r from-blue-600 to-purple-600' },
    { label: 'Green to Teal', value: 'bg-gradient-to-r from-green-600 to-teal-600' },
    { label: 'Orange to Red', value: 'bg-gradient-to-r from-orange-600 to-red-600' },
    { label: 'Purple to Pink', value: 'bg-gradient-to-r from-purple-600 to-pink-600' },
    { label: 'Indigo to Blue', value: 'bg-gradient-to-r from-indigo-600 to-blue-600' },
    { label: 'Amber to Yellow', value: 'bg-gradient-to-r from-amber-600 to-yellow-600' },
    { label: 'Yellow to Orange', value: 'bg-gradient-to-r from-yellow-600 to-orange-600' },
    { label: 'Emerald to Green', value: 'bg-gradient-to-r from-emerald-600 to-green-600' },
    { label: 'Rose to Pink', value: 'bg-gradient-to-r from-rose-600 to-pink-600' },
    { label: 'Violet to Purple', value: 'bg-gradient-to-r from-violet-600 to-purple-600' },
    { label: 'Pink to Rose', value: 'bg-gradient-to-r from-pink-600 to-rose-600' },
    { label: 'Cyan to Blue', value: 'bg-gradient-to-r from-cyan-600 to-blue-600' },
    { label: 'Fuchsia to Purple', value: 'bg-gradient-to-r from-fuchsia-600 to-purple-600' }
  ];

  useEffect(() => {
    loadTeamMembers();
  }, []);

  const loadTeamMembers = async () => {
    try {
      setLoading(true);
      const result = await getTeamMembers();
      if (result.success) {
        setTeamMembers(result.data || []);
      } else {
        toast.error('Failed to load team members');
      }
    } catch (error) {
      toast.error('Error loading team members');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate avatar from name if not set
    if (field === 'name' && !formData.avatar) {
      const names = value.trim().split(' ');
      if (names.length >= 2) {
        const initials = (names[0][0] + names[names.length - 1][0]).toUpperCase();
        setFormData(prev => ({ ...prev, avatar: initials }));
      } else if (names.length === 1 && names[0].length >= 2) {
        const initials = names[0].substring(0, 2).toUpperCase();
        setFormData(prev => ({ ...prev, avatar: initials }));
      }
    }
  };

  const handleEdit = (member) => {
    setFormData({
      name: member.name || '',
      role: member.role || '',
      position: member.position || 'Development',
      avatar: member.avatar || '',
      color: member.color || 'bg-gradient-to-r from-blue-600 to-purple-600',
      order: member.order || 0
    });
    setEditingId(member.id);
    setShowForm(true);
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      role: '',
      position: 'Development',
      avatar: '',
      color: 'bg-gradient-to-r from-blue-600 to-purple-600',
      order: 0
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter team member name');
      return;
    }
    if (!formData.role.trim()) {
      toast.error('Please enter team member role');
      return;
    }
    if (!formData.avatar.trim()) {
      toast.error('Please enter avatar initials');
      return;
    }

    try {
      setSaving(true);
      const result = editingId
        ? await saveTeamMember({ ...formData, id: editingId })
        : await saveTeamMember({ ...formData, order: teamMembers.length });

      if (result.success) {
        toast.success(editingId ? 'Team member updated successfully' : 'Team member added successfully');
        handleCancel();
        loadTeamMembers();
      } else {
        toast.error('Failed to save: ' + result.error);
      }
    } catch (error) {
      toast.error('Error saving team member: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this team member?')) {
      return;
    }

    try {
      const result = await deleteTeamMember(id);
      if (result.success) {
        toast.success('Team member deleted successfully');
        loadTeamMembers();
      } else {
        toast.error('Failed to delete: ' + result.error);
      }
    } catch (error) {
      toast.error('Error deleting team member: ' + error.message);
    }
  };

  const handleMigrateExistingMembers = async () => {
    if (!window.confirm('This will add all existing team members from the homepage to Firebase. Continue?')) {
      return;
    }

    // Existing team members from Home.js fallback
    const existingMembers = [
      {
        name: 'Abhishek Sharma',
        role: 'Founder & CEO',
        position: 'Leadership',
        avatar: 'AS',
        color: 'bg-gradient-to-r from-blue-600 to-purple-600',
        order: 0
      },
      {
        name: 'Mahima Kumari',
        role: 'UI/UX Designer & Graphic Designer',
        position: 'Design',
        avatar: 'MK',
        color: 'bg-gradient-to-r from-green-600 to-teal-600',
        order: 1
      },
      {
        name: 'Mayank Kumawat',
        role: 'Marketing Manager',
        position: 'Marketing',
        avatar: 'MK',
        color: 'bg-gradient-to-r from-orange-600 to-red-600',
        order: 2
      },
      {
        name: 'Hariram Yadav',
        role: 'Database Administrator',
        position: 'Database',
        avatar: 'HY',
        color: 'bg-gradient-to-r from-purple-600 to-pink-600',
        order: 3
      },
      {
        name: 'Vasu Agarwal',
        role: 'Content Manager',
        position: 'Content',
        avatar: 'VA',
        color: 'bg-gradient-to-r from-indigo-600 to-blue-600',
        order: 4
      },
      {
        name: 'Harsh Kumawat',
        role: 'Software Engineer',
        position: 'Development',
        avatar: 'HK',
        color: 'bg-gradient-to-r from-amber-600 to-yellow-600',
        order: 5
      },
      {
        name: 'Richa Shrivastava',
        role: 'Backend Engineer',
        position: 'Development',
        avatar: 'RS',
        color: 'bg-gradient-to-r from-yellow-600 to-orange-600',
        order: 6
      },
      {
        name: 'Samarth Sharma',
        role: 'Content Writer',
        position: 'Content',
        avatar: 'SS',
        color: 'bg-gradient-to-r from-emerald-600 to-green-600',
        order: 7
      },
      {
        name: 'Khushi Sharma',
        role: 'Marketing Executive',
        position: 'Marketing',
        avatar: 'KS',
        color: 'bg-gradient-to-r from-rose-600 to-pink-600',
        order: 8
      },
      {
        name: 'Sandeep Sharma',
        role: 'Business Onboarding',
        position: 'Business',
        avatar: 'SS',
        color: 'bg-gradient-to-r from-violet-600 to-purple-600',
        order: 9
      },
      {
        name: 'Muskan Chaudhary',
        role: 'Social Media Handler',
        position: 'Marketing',
        avatar: 'MC',
        color: 'bg-gradient-to-r from-pink-600 to-rose-600',
        order: 10
      },
      {
        name: 'Tanishka',
        role: 'Content Writer',
        position: 'Content',
        avatar: 'TA',
        color: 'bg-gradient-to-r from-cyan-600 to-blue-600',
        order: 11
      },
      {
        name: 'Akshita Sharma',
        role: 'Institute Onboarding',
        position: 'Business',
        avatar: 'AK',
        color: 'bg-gradient-to-r from-fuchsia-600 to-purple-600',
        order: 12
      }
    ];

    try {
      setSaving(true);
      let successCount = 0;
      let errorCount = 0;

      // Check which members already exist (by name) to avoid duplicates
      const existingNames = teamMembers.map(m => m.name.toLowerCase());

      for (const member of existingMembers) {
        // Skip if member already exists
        if (existingNames.includes(member.name.toLowerCase())) {
          continue;
        }

        const result = await saveTeamMember(member);
        if (result.success) {
          successCount++;
        } else {
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully migrated ${successCount} team member(s) to Firebase`);
      }
      if (errorCount > 0) {
        toast.error(`Failed to migrate ${errorCount} team member(s)`);
      }
      if (successCount === 0 && errorCount === 0) {
        toast.info('All team members are already in Firebase');
      }

      loadTeamMembers();
    } catch (error) {
      toast.error('Error migrating team members: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Team Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Manage team members displayed on the homepage
          </p>
        </div>
        <div className="flex items-center gap-3">
          {teamMembers.length === 0 && (
            <button
              onClick={handleMigrateExistingMembers}
              disabled={saving}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
              title="Import all existing team members from homepage to Firebase"
            >
              <Download className="w-5 h-5" />
              {saving ? 'Migrating...' : 'Migrate Existing Members'}
            </button>
          )}
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Team Member
          </button>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {editingId ? 'Edit Team Member' : 'Add New Team Member'}
            </h2>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Full Name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role *
                </label>
                <input
                  type="text"
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Software Engineer"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Position
                </label>
                <select
                  value={formData.position}
                  onChange={(e) => handleInputChange('position', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {positions.map(pos => (
                    <option key={pos} value={pos}>{pos}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Avatar Initials *
                </label>
                <input
                  type="text"
                  value={formData.avatar}
                  onChange={(e) => handleInputChange('avatar', e.target.value.toUpperCase())}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., AS"
                  maxLength={2}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Avatar Color Gradient
              </label>
              <select
                value={formData.color}
                onChange={(e) => handleInputChange('color', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {colorOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Display Order
              </label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => handleInputChange('order', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="0"
                min="0"
              />
              <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
            </div>

            {/* Preview */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Preview
              </label>
              <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${formData.color}`}>
                  <span className="text-white font-bold text-lg">{formData.avatar || 'XX'}</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    {formData.name || 'Team Member Name'}
                  </h4>
                  <p className="text-sm text-primary-600 dark:text-primary-400">
                    {formData.role || 'Role'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formData.position || 'Position'}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Banner */}
      {teamMembers.length === 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <Users className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">
                Getting Started
              </h3>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                No team members in Firebase yet. Click "Migrate Existing Members" to import all current team members from the homepage, or add new members manually.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Team Members List */}
      {teamMembers.length === 0 && !showForm ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 mb-4">No team members in Firebase yet.</p>
          <p className="text-sm text-gray-500 dark:text-gray-500">
            Use "Migrate Existing Members" button above to import all current team members, or add new members manually.
          </p>
        </div>
      ) : (
        !showForm && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teamMembers.map((member) => (
              <div key={member.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${member.color}`}>
                    <span className="text-white font-bold text-lg">{member.avatar}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {member.name}
                        </h3>
                        <p className="text-sm text-primary-600 dark:text-primary-400 font-medium">
                          {member.role}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {member.position}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(member)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(member.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Order: {member.order || 0}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default TeamManagement;

